import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Badge, ListGroup, Alert, ButtonGroup } from 'react-bootstrap';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
    FaArrowUp,
    FaArrowDown,
    FaTrash,
    FaPlus,
    FaSave,
    FaHistory,
    FaDownload,
    FaEye,
    FaPaperclip,
} from 'react-icons/fa';
import { Editor } from '@hugerte/hugerte-react';
import './TermsAdmin.css';
import { create, getActive, getAll, getAnalytics } from 'Services/TermsService';
import { getMediaUrl } from 'Utils/Helpers';

const TermsAdmin = () => {
    const [analytics, setAnalytics] = useState(null);
    const [termsList, setTermsList] = useState([]);
    const [activeTerms, setActiveTerms] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [readOnly, setReadOnly] = useState(false);
    const [initialValues, setInitialValues] = useState({
        tabs: [{ title: '', content: '', order: 0 }],
        isNewVersion: true,
        versionType: 1,
        changeDescription: '',
    });

    useEffect(() => {
        loadTermsList();
        loadActiveTerms();
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        try {
            const response = await getAnalytics();
            setAnalytics(response.data);
        } catch (err) {
            console.error('Failed to load analytics', err);
        }
    };

    const validationSchema = Yup.object().shape({
        tabs: Yup.array()
            .of(
                Yup.object().shape({
                    title: Yup.string().required('Tab title is required').max(200, 'Title is too long'),
                    content: Yup.string().required('Content is required').min(10, 'Content is too short'),
                }),
            )
            .min(1, 'At least one tab is required'),
        isNewVersion: Yup.boolean().required(),
        versionType: Yup.number().when('isNewVersion', {
            is: true,
            then: Yup.number().required('Version type is required when creating new version'),
        }),
        changeDescription: Yup.string().max(500, 'Description is too long'),
    });

    const loadTermsList = async () => {
        try {
            const response = await getAll();
            setTermsList(response.data);
        } catch (err) {
            console.error('Failed to load terms list', err);
            toast.error('Failed to load terms history');
        }
    };

    const loadActiveTerms = async () => {
        try {
            const response = await getActive();
            setActiveTerms(response.data);
        } catch (err) {
            console.error('Failed to load active terms', err);
        }
    };

    const loadTermsVersion = (term) => {
        setInitialValues({
            tabs: term.tabs.map((tab) => ({
                title: tab.title,
                content: tab.content,
                order: tab.order,
            })),
            isNewVersion: false,
            versionType: 1,
            changeDescription: '',
        });
        setSelectedFile(null);
        setFileInputKey((k) => k + 1);
        setReadOnly(!term.isActive);
        toast.info(`Loaded version ${term.version}`);
    };

    const calculatePreviewVersion = (versionType) => {
        if (!activeTerms) return '1.0.0';

        const parts = activeTerms.version.split('.');
        let major = parseInt(parts[0]) || 0;
        let minor = parseInt(parts[1]) || 0;
        let patch = parseInt(parts[2]) || 0;

        switch (parseInt(versionType)) {
            case 0: // Major
                major++;
                minor = 0;
                patch = 0;
                break;
            case 1: // Minor
                minor++;
                patch = 0;
                break;
            case 2: // Patch
                patch++;
                break;
        }

        return `${major}.${minor}.${patch}`;
    };
    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setLoading(true);

        if (!values.isNewVersion && !activeTerms) {
            toast.error('No active terms to update');
            setSubmitting(false);
            setLoading(false);
            return;
        }

        try {
            const payload = {
                tabs: values.tabs.map((tab, index) => ({
                    title: tab.title,
                    content: tab.content,
                    order: index,
                })),
                isNewVersion: values.isNewVersion,
                versionType: values.versionType,
                changeDescription: values.changeDescription || null,
            };

            await create(payload, selectedFile);

            const message = values.isNewVersion
                ? `New version ${calculatePreviewVersion(values.versionType)} created successfully!`
                : 'Terms updated successfully!';
            toast.success(message);

            await loadTermsList();
            await loadActiveTerms();

            if (values.isNewVersion) {
                resetForm();
                setSelectedFile(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save terms');
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-0">
                        <FaHistory className="me-2" />
                        Terms & Conditions Management
                    </h1>
                </Col>
            </Row>

            {analytics && (
                <Card className="mb-4">
                    <Card.Header>Acceptance Analytics</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4}>
                                <div className="text-center">
                                    <h3>{analytics.totalUsers}</h3>
                                    <small>Total Users</small>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <h3>{analytics.acceptedUsers}</h3>
                                    <small>Accepted</small>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <h3>{analytics.acceptanceRate}%</h3>
                                    <small>Acceptance Rate</small>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Row>
                <Col lg={8}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Create or Update Terms</h5>
                            {readOnly && (
                                <Badge bg="warning" text="dark">
                                    Read Only
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                                enableReinitialize
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleSubmit,
                                    setFieldValue,
                                    isSubmitting,
                                }) => (
                                    <Form onSubmit={handleSubmit}>
                                        {/* Version Type Selection */}
                                        <Card className="mb-4 bg-light">
                                            <Card.Body>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold">Version Type</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        <Form.Check
                                                            type="radio"
                                                            id="newVersion"
                                                            label="Create New Version"
                                                            name="isNewVersion"
                                                            checked={values.isNewVersion === true}
                                                            onChange={() => {
                                                                setFieldValue('isNewVersion', true);
                                                            }}
                                                        />
                                                        <Form.Check
                                                            type="radio"
                                                            id="correction"
                                                            label="Update Current (Correction)"
                                                            name="isNewVersion"
                                                            checked={values.isNewVersion === false}
                                                            onChange={() => setFieldValue('isNewVersion', false)}
                                                        />
                                                    </div>
                                                </Form.Group>

                                                {/* Version Increment Type - Only show when creating new version */}
                                                {values.isNewVersion && (
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-bold">
                                                            Version Increment Type
                                                            {activeTerms && (
                                                                <span className="ms-2 text-muted">
                                                                    (Current: {activeTerms.version} → New:{' '}
                                                                    {calculatePreviewVersion(values.versionType)})
                                                                </span>
                                                            )}
                                                        </Form.Label>
                                                        <ButtonGroup className="d-flex">
                                                            <Button
                                                                variant={
                                                                    values.versionType === 0
                                                                        ? 'primary'
                                                                        : 'outline-primary'
                                                                }
                                                                onClick={() => {
                                                                    setFieldValue('versionType', 0);
                                                                }}
                                                            >
                                                                Major (X.0.0)
                                                                <br />
                                                                <small>Breaking changes</small>
                                                            </Button>
                                                            <Button
                                                                variant={
                                                                    values.versionType === 1
                                                                        ? 'primary'
                                                                        : 'outline-primary'
                                                                }
                                                                onClick={() => {
                                                                    setFieldValue('versionType', 1);
                                                                }}
                                                            >
                                                                Minor (0.X.0)
                                                                <br />
                                                                <small>New features</small>
                                                            </Button>
                                                            <Button
                                                                variant={
                                                                    values.versionType === 2
                                                                        ? 'primary'
                                                                        : 'outline-primary'
                                                                }
                                                                onClick={() => {
                                                                    setFieldValue('versionType', 2);
                                                                }}
                                                            >
                                                                Patch (0.0.X)
                                                                <br />
                                                                <small>Bug fixes</small>
                                                            </Button>
                                                        </ButtonGroup>
                                                        {touched.versionType && errors.versionType && (
                                                            <div className="text-danger small mt-2">
                                                                {errors.versionType}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                )}
                                            </Card.Body>
                                        </Card>

                                        {/* Change Description */}
                                        <Form.Group className="mb-4">
                                            <Form.Label>Change Description (Optional)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="changeDescription"
                                                value={values.changeDescription}
                                                onChange={handleChange}
                                                placeholder="Describe what changed..."
                                                isInvalid={touched.changeDescription && errors.changeDescription}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.changeDescription}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {/* Attached file */}
                                        <Form.Group className="mb-4" controlId="terms-file-input">
                                            <Form.Label>
                                                <FaPaperclip className="me-2" />
                                                Fichier joint (PDF, image…) — optionnel
                                            </Form.Label>
                                            <Form.Control
                                                key={fileInputKey}
                                                type="file"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                onChange={(e) => setSelectedFile(e.currentTarget.files[0] || null)}
                                            />
                                            {selectedFile && (
                                                <Form.Text className="text-success">
                                                    Fichier sélectionné : {selectedFile.name}
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                        {/* Tabs Section */}
                                        <FieldArray name="tabs">
                                            {({ push, remove, move }) => (
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5 className="mb-0">
                                                            <FaPlus className="me-2" />
                                                            Tabs
                                                        </h5>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={() =>
                                                                push({
                                                                    title: '',
                                                                    content: '',
                                                                    order: values.tabs.length,
                                                                })
                                                            }
                                                        >
                                                            <FaPlus className="me-2" />
                                                            Add Tab
                                                        </Button>
                                                    </div>

                                                    {values.tabs.map((tab, index) => (
                                                        <Card key={index} className="mb-3 tab-card">
                                                            <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                                                                <span className="fw-bold">Tab {index + 1}</span>
                                                                <div className="d-flex gap-2">
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={index === 0}
                                                                        onClick={() => move(index, index - 1)}
                                                                    >
                                                                        <FaArrowUp />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={index === values.tabs.length - 1}
                                                                        onClick={() => move(index, index + 1)}
                                                                    >
                                                                        <FaArrowDown />
                                                                    </Button>
                                                                    {values.tabs.length > 1 && (
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => remove(index)}
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </Card.Header>
                                                            <Card.Body>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label>Tab Title *</Form.Label>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name={`tabs.${index}.title`}
                                                                        value={tab.title}
                                                                        onChange={handleChange}
                                                                        placeholder="e.g., Introduction"
                                                                        isInvalid={
                                                                            touched.tabs?.[index]?.title &&
                                                                            errors.tabs?.[index]?.title
                                                                        }
                                                                    />
                                                                    <Form.Control.Feedback type="invalid">
                                                                        {errors.tabs?.[index]?.title}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>

                                                                <Form.Group>
                                                                    <Form.Label>Content *</Form.Label>
                                                                    <Editor
                                                                        value={tab.content || ''}
                                                                        onEditorChange={(content) =>
                                                                            setFieldValue(
                                                                                `tabs.${index}.content`,
                                                                                content,
                                                                            )
                                                                        }
                                                                        init={{
                                                                            menubar: false,
                                                                            plugins: 'table link lists code',
                                                                            toolbar:
                                                                                'undo redo | bold italic underline | bullist numlist | link | ' +
                                                                                'table tabledelete | tableprops tablerowprops tablecellprops | ' +
                                                                                'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                                                                                'tableinsertcolbefore tableinsertcolafter tabledeletecol | code',
                                                                            height: 320,
                                                                        }}
                                                                        className={
                                                                            touched.tabs?.[index]?.content &&
                                                                            errors.tabs?.[index]?.content
                                                                                ? 'is-invalid-wysiwyg'
                                                                                : ''
                                                                        }
                                                                    />
                                                                    {touched.tabs?.[index]?.content &&
                                                                        errors.tabs?.[index]?.content && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {errors.tabs?.[index]?.content}
                                                                            </div>
                                                                        )}
                                                                </Form.Group>
                                                            </Card.Body>
                                                        </Card>
                                                    ))}

                                                    {typeof errors.tabs === 'string' && (
                                                        <Alert variant="danger">{errors.tabs}</Alert>
                                                    )}
                                                </div>
                                            )}
                                        </FieldArray>

                                        {/* Submit Button */}
                                        {!readOnly && (
                                            <div className="d-flex justify-content-end mt-4">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    size="lg"
                                                    disabled={loading || isSubmitting}
                                                >
                                                    <FaSave className="me-2" />
                                                    {loading || isSubmitting
                                                        ? 'Saving...'
                                                        : values.isNewVersion
                                                        ? `Create Version ${calculatePreviewVersion(
                                                              values.versionType,
                                                          )}`
                                                        : 'Update Current Version'}
                                                </Button>
                                            </div>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Version History Sidebar */}
                <Col lg={4}>
                    <Card className="shadow-sm sticky-top" style={{ top: '20px' }}>
                        <Card.Header className="bg-secondary text-white">
                            <h5 className="mb-0">
                                <FaHistory className="me-2" />
                                Version History
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {termsList.length === 0 ? (
                                <div className="text-center text-muted p-4">No versions yet</div>
                            ) : (
                                <ListGroup variant="flush">
                                    {termsList.map((term) => (
                                        <ListGroup.Item
                                            key={term.id}
                                            className={term.isActive ? 'border-start border-success border-4' : ''}
                                        >
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <strong className="text-primary">Version {term.version}</strong>
                                                {term.isActive && (
                                                    <Badge bg="success" pill>
                                                        Active
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="small text-muted mb-2">
                                                <div>{term.isNewVersion ? 'New Version' : 'Correction'}</div>
                                                <div>{new Date(term.createdDate).toLocaleDateString()}</div>
                                                <div>By: {term.createdBy}</div>
                                            </div>
                                            {term.changeDescription && (
                                                <p className="small fst-italic mb-2">{term.changeDescription}</p>
                                            )}
                                            {term.fileUrl && (
                                                <div className="d-flex gap-2 mb-2">
                                                    <a
                                                        href={getMediaUrl(term.fileUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        title="Voir"
                                                    >
                                                        <FaEye className="me-1" /> Voir
                                                    </a>
                                                    <a
                                                        href={getMediaUrl(term.fileUrl)}
                                                        download
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                        title="Télécharger"
                                                    >
                                                        <FaDownload className="me-1" /> Télécharger
                                                    </a>
                                                </div>
                                            )}
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() => loadTermsVersion(term)}
                                            >
                                                Load this version
                                            </Button>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default TermsAdmin;
